import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Countdown from 'react-countdown-now';
import A11yFontIcon from 'components/A11yFontIcon';
import GameChat from 'components/GameChat';
import LoadingIndicator from 'components/LoadingIndicator';
import QuestItemImage from 'components/QuestItem/QuestItemImage';
import QuestProgressBar from 'components/QuestItem/QuestProgressBar';
import TextButton from 'components/TextButton';
import { fromIso9075DateString } from 'utils/dates';
import { formatDate, padNumber } from 'utils/formatting';
import ModalCloseIcon from 'resources/icon-close-white.svg';
import withQuestLeaderboardData from './withQuestLeaderboardData';
import './QuestLeaderboardModal.css';

const GAME_CHAT_HISTORY_SEC = 7 * 24 * 60 * 60; // 7 days
const COUNTDOWN_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

const countdownRenderer = ({ label, days, hours, minutes, seconds }) => {
  return (
    <div>
      {label}:{' '}
      {!!days && (
        <>
          <span>{padNumber(days)}</span>:
        </>
      )}
      {!!(days || hours) && (
        <>
          <span>{padNumber(hours)}</span>:
        </>
      )}
      <span>{padNumber(minutes)}</span>:<span>{padNumber(seconds)}</span>
    </div>
  );
};

function renderLeaderboard({ loading, error, questLeaderboard }) {
  let content = null;
  if (loading) {
    content = <LoadingIndicator expand />;
  } else if (error || !questLeaderboard) {
    content = (
      <div className="leaderboard-message">
        Failed to retrieve leaderboard. Please try again later.
      </div>
    );
  } else {
    const myUserId =
      questLeaderboard.myRanking && questLeaderboard.myRanking.user.userId;
    const winningPlaces = questLeaderboard.winningPlaceCount;
    content = (
      <div className="top-ranking-rows">
        {questLeaderboard.rankings.map((ranking, index) =>
          renderRanking(
            ranking,
            ranking.user.userId === myUserId,
            winningPlaces,
            false
          )
        )}
        {!questLeaderboard.rankings.length && (
          <div className="leaderboard-message">
            No progress has been recorded yet.
          </div>
        )}
      </div>
    );
  }
  return <div className="quest-leaderboard-table">{content}</div>;
}

function renderRanking(ranking, highlight, winningPlaces, yourScore = false) {
  const { user, rank, displayScore, score, isComplete } = ranking;
  const winning = isComplete && rank <= winningPlaces;
  const classes =
    (winning ? ' winning-place' : '') +
    (highlight ? ' highlight' : '') +
    (yourScore ? ' your-score' : '');
  const displayName = yourScore ? 'YOUR SCORE' : user.displayName;
  return (
    <div className={`ranking-row${classes}`} key={user.userId}>
      <div className="ranking-name" title={displayName}>
        {displayName}
      </div>
      <QuestProgressBar
        ratio={score}
        isComplete={isComplete}
        icon={user.avatarUrl}
      />
      <div className="ranking-percent">{displayScore}</div>
      <div className="ranking-rank">
        <div className="rank-bubble">{rank}</div>
      </div>
    </div>
  );
}

function renderMyRanking({ loading, error, questLeaderboard }) {
  const ranking = questLeaderboard && questLeaderboard.myRanking;
  if (!loading && !error && ranking) {
    const winningPlaces = questLeaderboard.winningPlaceCount;
    return renderRanking(ranking, true, winningPlaces, true);
  } else {
    return <div className="no-your-score-row" />;
  }
}

function renderLeaderboardDescription(quest) {
  return (
    !!quest.leaderboardDescription && (
      <div className="quest-leaderboard-description">
        {quest.leaderboardDescription}
      </div>
    )
  );
}

function renderQuestDetails(quest) {
  const questDescription = quest.prizeDetails && quest.prizeDetails.description;
  return (
    <div className="quest-details">
      <QuestItemImage quest={quest} />
      <div className="quest-description">{questDescription}</div>
    </div>
  );
}

function renderQuestDates(endDateUtc, claimEndDate, leaderboardData) {
  const { questLeaderboard } = leaderboardData;
  const endDate = endDateUtc && fromIso9075DateString(endDateUtc);
  const updatedDate =
    questLeaderboard && fromIso9075DateString(questLeaderboard.lastUpdated);
  let endContent = null;
  if (!endDate) {
    endContent = !!claimEndDate && <div>Ends: {claimEndDate}</div>;
  } else if (endDate <= Date.now()) {
    endContent = <div>Leaderboard Ended</div>;
  } else if (endDate - Date.now() < COUNTDOWN_THRESHOLD_MS) {
    endContent = (
      <Countdown date={endDate} renderer={countdownRenderer} label="Ends In" />
    );
  } else {
    endContent = <div>Ends: {formatDate(endDate, 'fullLocalTimeNoYear')}</div>;
  }

  return (
    <div className="quest-dates">
      <div>Last Updated: {formatDate(updatedDate, 'fullLocalTimeNoYear')}</div>
      {endContent}
    </div>
  );
}

function renderQuestChat(quest, leaderboardData) {
  const { questLeaderboard } = leaderboardData;
  const totalPlayers = questLeaderboard && questLeaderboard.totalPlayers;

  return (
    <div className="quest-chat">
      <div className="quest-chat-header">
        <div className="quest-chat-count">
          {!!(totalPlayers || totalPlayers === 0) &&
            `${totalPlayers} Players Participating`}
        </div>
      </div>
      <GameChat
        room={`quest_${quest.id}`}
        historyDurationSec={GAME_CHAT_HISTORY_SEC}
        showTime={true}
      />
    </div>
  );
}

function QuestLeaderboardModal({
  show,
  quest,
  endDateUtc,
  claimEndDate,
  onClose,
  leaderboardData,
  subscribeToLeaderboardUpdates
}) {
  const questName = quest.prizeDetails ? quest.prizeDetails.name : 'N/A';
  return (
    !!show && (
      <Modal
        dialogClassName="quest-leaderboard-modal"
        onHide={onClose}
        show={show}
        backdrop={true}
      >
        <Modal.Header>
          <div>
            {questName} Leaderboard&nbsp;
            <A11yFontIcon icon="trophy" alt="" />
          </div>
          <TextButton className="modal-close-icon" onClick={onClose}>
            <img src={ModalCloseIcon} alt="Close Leadboard" />
          </TextButton>
        </Modal.Header>
        <Modal.Body>
          <div className="left-panel">
            {renderQuestDetails(quest)}
            {renderQuestChat(quest, leaderboardData)}
          </div>
          <div className="right-panel">
            {renderQuestDates(endDateUtc, claimEndDate, leaderboardData)}
            {renderLeaderboard(leaderboardData)}
            {renderMyRanking(leaderboardData)}
            {renderLeaderboardDescription(quest)}
          </div>
        </Modal.Body>
      </Modal>
    )
  );
}

QuestLeaderboardModal.propTypes = {
  quest: PropTypes.object,
  endDateUtc: PropTypes.string,
  claimEndDate: PropTypes.string,
  onClose: PropTypes.func,
  show: PropTypes.bool
};

export default withQuestLeaderboardData(QuestLeaderboardModal);
